/** CSS contract: css-library/components/02-containers/pill.css. */
import type { Ref } from 'react';
import { type ButtonHTMLAttributes, type HTMLAttributes, type MouseEvent } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';
import { Menu, MenuRow } from './Menu';
import { Pill } from './Pill';
import { PillCluster } from './PillCluster';

export interface TabsItem {
  name: string;
  label: string;
  icon?: string;
  title?: string;
  disabled?: boolean;
  completed?: boolean;
  htmlProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onChange'> {
  items: TabsItem[];
  activeName: string;
  onChange: (name: string) => void;
  onItemClick?: (name: string, event: MouseEvent<HTMLButtonElement>) => void;
}

export function Tabs(
  { ref, items, activeName, onChange, onItemClick, ...rest }: TabsProps & { ref?: Ref<HTMLDivElement> },
) {
  const active = items.find((item) => item.name === activeName);
  if (!items.length) return null;
  if (items.length === 1) {
    const item = items[0];
    const content = <><Icon name={item.icon ?? 'i-list'} /><span className="bar-title">{item.label}</span></>;
    return <PillCluster ref={ref} {...rest}><Pill>{active
      ? <span className="btn btn--static is-active" aria-current="page"
          aria-label={item.htmlProps?.['aria-label']} aria-labelledby={item.htmlProps?.['aria-labelledby']}>{content}</span>
      : <Button {...item.htmlProps} disabled={item.disabled ?? item.htmlProps?.disabled} title={item.title} onClick={(event) => {
          item.htmlProps?.onClick?.(event);
          if (!event.defaultPrevented) onItemClick?.(item.name, event);
          if (!event.defaultPrevented) onChange(item.name);
        }}>{content}</Button>}
    </Pill></PillCluster>;
  }
  return <PillCluster ref={ref} {...rest}>
    <Menu className="tab-menu" presentation="pill" isList trigger={({ triggerProps }) => <Pill>
      <Button {...triggerProps} isActive disabled={active?.disabled ?? active?.htmlProps?.disabled} aria-label={active?.htmlProps?.['aria-label'] ?? active?.label ?? 'Ansicht auswählen'} aria-labelledby={active?.htmlProps?.['aria-labelledby']}>
        <Icon name={active?.icon ?? 'i-list'} />
        <span className="bar-title">{active?.label ?? 'Ansicht auswählen'}</span>
        <Icon name="i-chevron-down" />
      </Button>
    </Pill>}>
      {items.map((item) => <MenuRow
        {...Object.fromEntries(Object.entries(item.htmlProps ?? {}).filter(([name]) => name !== 'onSelect'))}
        key={item.name}
        label={item.label}
        icon={item.icon ?? 'i-list'}
        title={item.title}
        role="menuitemradio"
        aria-checked={item.name === activeName}
        disabled={item.disabled ?? item.htmlProps?.disabled}
        isSelected={item.name === activeName}
        isCompleted={item.completed}
        onClick={(event) => {
          item.htmlProps?.onClick?.(event);
          if (!event.defaultPrevented) onItemClick?.(item.name, event);
          if (!event.defaultPrevented) onChange(item.name);
        }}
      />)}
    </Menu>
  </PillCluster>;
}
